import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/features/admin/api/adminApi';
import { placesApi } from '@/features/places/api/placesApi';
import { Place } from '@/types/place';

/**
 * Custom hook for admin place management with TanStack Query.
 */
export const useAdminPlaces = () => {
  const queryClient = useQueryClient();

  // Fetch all places
  const { data: places = [], isLoading, isError, error, refetch } = useQuery<Place[]>({
    queryKey: ['adminPlaces'],
    queryFn: () => adminApi.getAllPlaces(),
    retry: 1,
  });

  // Approve place mutation
  const approveMutation = useMutation({
    mutationFn: (placeId: string) => adminApi.approvePlace(placeId),
    onSuccess: () => {
      // Invalidate queries to refetch
      queryClient.invalidateQueries({ queryKey: ['adminPlaces'] });
      queryClient.invalidateQueries({ queryKey: ['places'] });
    },
  });

  // Reject place mutation
  const rejectMutation = useMutation({
    mutationFn: (placeId: string) => adminApi.rejectPlace(placeId),
    onSuccess: () => {
      // Invalidate queries to refetch
      queryClient.invalidateQueries({ queryKey: ['adminPlaces'] });
      queryClient.invalidateQueries({ queryKey: ['places'] });
    },
  });

  // Update place status (for status changes)
  const updateStatusMutation = useMutation({
    mutationFn: ({ placeId, status }: { placeId: string; status: string }) =>
      placesApi.update(placeId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminPlaces'] });
      queryClient.invalidateQueries({ queryKey: ['places'] });
    },
  });

  // Update place VIP status
  const updateVipMutation = useMutation({
    mutationFn: ({ placeId, vip }: { placeId: string; vip: boolean }) =>
      placesApi.update(placeId, { vip }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminPlaces'] });
      queryClient.invalidateQueries({ queryKey: ['places'] });
    },
  });

  return {
    places,
    isLoading,
    isError,
    error,
    refetch,
    approvePlace: (placeId: string) => approveMutation.mutate(placeId),
    rejectPlace: (placeId: string) => rejectMutation.mutate(placeId),
    updateStatus: (placeId: string, status: string) =>
      updateStatusMutation.mutate({ placeId, status }),
    updateVip: (placeId: string, vip: boolean) =>
      updateVipMutation.mutate({ placeId, vip }),
    isApproving: approveMutation.isPending,
    isRejecting: rejectMutation.isPending,
    isUpdatingStatus: updateStatusMutation.isPending,
    isUpdatingVip: updateVipMutation.isPending,
  };
};
